// Global
import { Field } from '@sitecore-jss/sitecore-jss-nextjs';
import { classnames } from 'tailwindcss-classnames';
// Generated
import { Sitecore } from '.generated/templates/_.Sitecore.Override';
// Lib

// Helpers
import Container from 'components/helpers/Container';
import Heading, { propsFromGraphqlData } from 'components/helpers/Heading';
import LinkA11yWrapper from 'components/helpers/LinkA11yWrapper';
import RichTextA11yWrapper from 'components/helpers/RichTextA11yWrapper';
import useDictionary from 'lib/sitecore/use-dictionary';
import ShowMoreListing from 'components/helpers/ShowMoreListing';
import { MissingDataSource } from 'components/helpers/EditingHelpText';

export type PageChild = {
  // This is needed for and used in ShowMoreListing component
  // eslint-disable-next-line react/no-unused-prop-types
  id: string;
  navigationTitle: {
    jsonValue: Field<string>;
  };
  pageTitle: {
    jsonValue: Field<string>;
  };
  pageDescription: {
    jsonValue: Field<string>;
  };
  // Not sure why ESLint thinks this is not used when it is.
  // eslint-disable-next-line react/no-unused-prop-types
  excludeFromNavigation: {
    jsonValue: Field<boolean>;
  };
  url: {
    url: string;
    path: string;
  };
};

export type PageParent = {
  childPages: PageChild[];
};

export type DynamicLinkListProps = {
  fields: {
    data: {
      datasource?: {
        headingText: { jsonValue: Field<string> };
        headingLevel: { jsonValue: Sitecore.Override.ItemEx };
        description?: { jsonValue: Field<string> };
        parentItem: {
          targetItem?: { children: { results: PageChild[] } } | null;
        };
        maxInitialDisplay: { jsonValue: Field<number> };
      };
    };
  };
};

const ChildItem = ({
  url,
  pageDescription,
  navigationTitle,
  pageTitle,
}: PageChild): JSX.Element => {
  const linkText = navigationTitle.jsonValue.value || pageTitle.jsonValue.value;
  return (
    <>
      <div className={classnames('inline-block')}>
        <LinkA11yWrapper
          className={classnames(
            'font-semibold',
            'text-theme-btn-link-text',
            'text-base',
            'leading-3',
            'sm:text-body',
            'hover:text-theme-btn-link-text-hover',
            'hover:underline'
          )}
          editable={false}
          field={{
            value: {
              href: url.path,
              text: linkText,
            },
          }}
        />
        <RichTextA11yWrapper
          className={classnames('rte', 'leading-3', 'pt-1')}
          field={pageDescription.jsonValue}
          editable={false}
        />
      </div>
    </>
  );
};

const PageListing = ({ fields }: DynamicLinkListProps): JSX.Element => {
  const dictionary = useDictionary();

  if (!fields) {
    return <MissingDataSource />;
  }

  const seeAllCtaText = dictionary.See_All_DynamicLinkList;
  const seeLessCtaText = dictionary.See_Less_DynamicLinkList;

  const datasource = fields.data.datasource;
  const maxInitialDisplay = datasource?.maxInitialDisplay.jsonValue.value || 6;
  const children = datasource?.parentItem?.targetItem?.children.results || [];

  const displayeditems = children.filter(
    // Check page title to see if it's actually a page.
    (page) => page.pageTitle && page.excludeFromNavigation?.jsonValue.value !== true
  );

  return (
    <Container size="standard" dataComponent="authorable/navigation/dynamiclinklist">
      <Heading {...propsFromGraphqlData(fields.data)} className={classnames('mb-4')} />
      <ShowMoreListing
        items={displayeditems}
        maxInitialDisplay={maxInitialDisplay}
        seeAllCtaClassName={classnames('mt-6')}
        seeAllCtaText={seeAllCtaText}
        seeLessCtaText={seeLessCtaText}
        component={ChildItem}
      />
    </Container>
  );
};

export default PageListing;
